import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { Button } from '@/components/ui/Button'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { useCrudList } from '@/hooks/useCrudList'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import { CompaniesService, type Company } from '@/services/modules/CompaniesService'
import { RevenuesService, type Revenue } from '@/services/modules/RevenuesService'
import { useRevenueActions } from '@/pages/Revenues/useRevenueActions'
import { useRevenueMaintenance } from '@/pages/Revenues/useRevenueMaintenance'
import { toast } from '@/components/ui/Toast'
import { AsaasBadge } from '@/components/financial/AsaasBadge'

/**
 * Faturas de uma empresa na visão da ADMINISTRAÇÃO — equivalente à tela de
 * faturas do assinante. Não confundir com /my-company/invoices, que é a mesma
 * informação na visão do próprio cliente (sem ações internas).
 */
export function CompanyInvoicesPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const [company, setCompany] = useState<Company | null>(null)
  const [reload, setReload] = useState(0)

  function refresh() { setReload(v => v + 1) }
  const { rowActions, dialogs } = useRevenueActions(refresh)
  const maintenance = useRevenueMaintenance({ companyId: id }, refresh)

  const filters = useMemo(() => ({ company_id: id }), [id])
  const { setPage, size, setSize, rows, meta, loading } = useCrudList(RevenuesService, { size: 25, reloadKey: reload, filters })

  useEffect(() => {
    if (!id) return
    CompaniesService.show(id).then(setCompany).catch(() => {
      toast.error({ title: 'Erro', description: 'Não foi possível carregar a empresa.' })
    })
  }, [id])

  const exp = useDataTableExport<Revenue>({
    filename: 'faturas-empresa',
    service: RevenuesService,
    filters,
    columns: [
      { header: 'Código',     accessor: (r: Revenue) => r.code },
      { header: 'Vencimento', accessor: (r: Revenue) => r.formatted?.dueDate ?? '' },
      { header: 'Pagamento',  accessor: (r: Revenue) => r.formatted?.paidDate ?? '' },
      { header: 'Valor',      accessor: (r: Revenue) => r.formatted?.amount ?? String(r.amount) },
      { header: 'Forma de pagamento', accessor: (r: Revenue) => r.method ?? '' },
      { header: 'Origem',     accessor: (r: Revenue) => r.hasAsaasCharge === true ? 'Asaas' : r.hasAsaasCharge === false ? 'Sistema' : 'Não verificado' },
      { header: 'Status',     accessor: (r: Revenue) => r.status },
    ],
  })

  const columns: DataTableColumn<Revenue>[] = [
    { key: 'due',    header: 'Vencimento', alwaysVisible: true, align: 'center', className: 'whitespace-nowrap', render: r => r.formatted?.dueDate || '—' },
    { key: 'paid',   header: 'Pagamento', align: 'center', className: 'whitespace-nowrap', render: r => r.formatted?.paidDate || '—' },
    { key: 'total',  header: 'Total', align: 'center', className: 'whitespace-nowrap font-medium', render: r => r.formatted?.amount || '—' },
    { key: 'method', header: 'Forma de pagamento', align: 'center', render: r => <span className="text-xs text-gray-600">{r.method || '—'}</span> },
    { key: 'origin', header: 'Origem', align: 'center', className: 'w-28', render: r => <AsaasBadge revenue={r} /> },
    { key: 'status', header: 'Status', align: 'center', className: 'w-28', render: r => <StatusBadge status={r.status} /> },
  ]

  return (
    <>
      <PageHeader
        title={`Faturas — ${company?.name ?? ''}`}
        subtitle="Faturas da empresa"
        crumbs={[{ label: 'Comercial' }, { label: 'Empresas' }, { label: 'Faturas' }]}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="secondary" iconLeft={<ArrowLeft size={14} />} onClick={() => navigate('/commercial/companies')}>Voltar</Button>
            {maintenance.toolbarAction}
          </div>
        }
      />
      <DataTable
        tableId="companies.invoices"
        exportable
        onExportPage={() => exp.exportPage(rows)}
        onExportAll={exp.exportAll}
        columns={columns}
        rows={rows}
        loading={loading}
        getRowKey={r => r.id}
        emptyMessage="Nenhuma fatura para esta empresa."
        rowActions={(r) => [...rowActions(r), ...maintenance.rowActions(r)]}
      />
      <Pagination meta={meta} onChange={setPage} size={size} onSizeChange={setSize} />

      {dialogs}
      {maintenance.dialogs}
    </>
  )
}
